// Port of SuiteModel/QuoteMarks.swift: quotation-mark problems in prose — straight quotes and apostrophes (curled by
// context), double quotes facing the wrong way (`fine!“`), and quotes never closed (a paragraph left open is fine when
// the next reopens the speech), and primes (a straight quote after a number for feet or inches: `5'10"`). Positions are
// Unicode-scalar offsets. Exact.
import { linkScalarRanges } from '../proseMarkdown.ts';
import { fromScalars, isAlphabetic, isDigit, isWhitespace, toScalars } from './scalars.ts';

export type QuoteProblemKind = 'wrong-way' | 'unclosed' | 'unopened' | 'straight' | 'prime';

export interface QuoteProblem {
  kind: QuoteProblemKind;
  /** The mark's position in the text (scalars). */
  offset: number;
  /** What a fix puts there (null when only the writer can fix it). */
  replacement: string | null;
}

/** `Problem.message`. */
export function quoteProblemMessage(p: QuoteProblem): string {
  switch (p.kind) {
    case 'wrong-way': return p.replacement === '”' ? 'An opening quote where the speech ends; it should close it.' : 'A closing quote where a speech begins; it should open it.';
    case 'unclosed': return 'This quote is never closed.';
    case 'unopened': return 'A closing quote with no opening quote before it.';
    case 'straight': return 'A straight quote.';
    case 'prime': return p.replacement === '″' ? 'Inches take a double prime (″), not a quotation mark.' : 'Feet take a prime (′), not an apostrophe.';
  }
}

export interface QuoteReport {
  problems: QuoteProblem[];
  wrongWay: number;
  /** Unclosed and unopened together. */
  unclosed: number;
  straight: number;
  primes: number;
  /** Everything a fix would change. */
  fixable: number;
  isClean: boolean;
}

export function quoteReport(problems: QuoteProblem[]): QuoteReport {
  const count = (f: (p: QuoteProblem) => boolean) => problems.filter(f).length;
  const wrongWay = count((p) => p.kind === 'wrong-way'), straight = count((p) => p.kind === 'straight'), primes = count((p) => p.kind === 'prime');
  return {
    problems, wrongWay, straight, primes, unclosed: count((p) => p.kind === 'unclosed' || p.kind === 'unopened'),
    fixable: wrongWay + straight + primes, isClean: problems.length === 0,
  };
}

/** Words that begin with an apostrophe standing for missing letters ('em, 'til, 'cause…). */
export const ELISIONS: ReadonlySet<string> = new Set(['em', 'til', 'tis', 'twas', 'twere', 'twill', 'cause', 'cos', 'bout', 'n', 'round', 'neath', 'nuff', 'ere', 'e']);
/** Emphasis markers are invisible to quote context: `*"Go!"*` opens and closes like `"Go!"`. */
export const QUOTE_MARKERS: ReadonlySet<string> = new Set(['*', '_', '\\']);
export const QUOTE_OPENERS: ReadonlySet<string> = new Set(['(', '[', '{', '—', '–', '-', '/', '‘', '“']);

/** `QuoteMarks.check`. */
export function checkQuotes(text: string): QuoteReport {
  return quoteReport(analyseQuotes(toScalars(text)));
}

/** `QuoteMarks.fixed`: every fixable problem fixed, scalar for scalar (offsets are unchanged). Unclosed quotes are left. */
export function fixedQuotes(text: string): string {
  return fromScalars(fixedQuoteScalars(toScalars(text)));
}

export function fixedQuoteScalars(s: string[]): string[] {
  const problems = analyseQuotes(s);
  if (!problems.some((p) => p.replacement !== null)) return s;
  const out = [...s];
  for (const p of problems) if (p.replacement !== null) out[p.offset] = toScalars(p.replacement)[0];
  return out;
}

/** `QuoteMarks.analyse`. */
export function analyseQuotes(s: readonly string[]): QuoteProblem[] {
  const problems: QuoteProblem[] = [];
  // A link's syntax (`[` and `](address)`) is invisible to quote context, and its address is never curled.
  const linkSyntax = new Set<number>();
  for (const link of linkScalarRanges(s)) for (const [from, to] of link.syntax) for (let k = from; k < to; k++) linkSyntax.add(k);
  const passedOver = (j: number) => QUOTE_MARKERS.has(s[j]) || linkSyntax.has(j);
  const paragraphs: [number, number][] = [];
  let start = 0;
  s.forEach((c, i) => {
    if (c === '\n') {
      paragraphs.push([start, i]);
      start = i + 1;
    }
  });
  paragraphs.push([start, s.length]);

  const isSpace = (c: string | undefined) => c === undefined || isWhitespace(c);
  const isWordChar = (c: string | undefined) => c !== undefined && (isAlphabetic(c) || isDigit(c));
  const previous = (i: number, p: [number, number]) => {
    let j = i - 1;
    while (j >= p[0] && passedOver(j)) j--;
    return j >= p[0] ? s[j] : undefined;
  };
  const next = (i: number, p: [number, number]) => {
    let j = i + 1;
    while (j < p[1] && passedOver(j)) j++;
    return j < p[1] ? s[j] : undefined;
  };
  /** Whether a double quote at `i` reads as opening from what's around it. */
  const opensHere = (i: number, p: [number, number]) => {
    const before = previous(i, p);
    return isSpace(before) || QUOTE_OPENERS.has(before!);
  };
  const firstMark = (p: [number, number]) => {
    let j = p[0];
    while (j < p[1] && (isSpace(s[j]) || passedOver(j))) j++;
    return j < p[1] ? s[j] : undefined;
  };

  /** Whether a straight quote at `i`, right after a digit, is a prime: `'` before a digit or (with no single quote opened
   *  before it in the paragraph) before a space, punctuation or the end; `"` after feet (`5'10"`, `5′10"`), or with no
   *  double quote open and no word after it (`a 12" record`). */
  const isPrime = (i: number, p: [number, number], doubleOpen: boolean, singleOpened: boolean): boolean => {
    if (!(i > p[0] && isDigit(s[i - 1]))) return false;
    const after = i + 1 < p[1] ? s[i + 1] : undefined;
    if (s[i] === "'") {
      if (isDigit(after)) return true;
      return !singleOpened && (after === undefined || isWhitespace(after) || after === '"' || [',', '.', ';', ':', '!', '?', ')'].includes(after));
    }
    let j = i - 1;
    while (j > p[0] && isDigit(s[j - 1])) j--;
    if (j - 1 > p[0] && (s[j - 1] === "'" || s[j - 1] === '′') && isDigit(s[j - 2])) return true;
    return !doubleOpen && !isWordChar(next(i, p));
  };

  paragraphs.forEach((p, n) => {
    let openAt: number | null = null;
    let singleOpened = false;
    for (let i = p[0]; i < p[1]; i++) {
      if (linkSyntax.has(i)) continue;
      const c = s[i];
      if ((c === '"' || c === "'") && isPrime(i, p, openAt !== null, singleOpened)) {
        problems.push({ kind: 'prime', offset: i, replacement: c === "'" ? '′' : '″' });
        continue;
      }
      if (c === '‘') singleOpened = true;
      if (c === '"' || c === '“' || c === '”') {
        const opening = opensHere(i, p);
        if (c === '"') {
          // Straight: curl by context, or by whether a speech is open when context can't tell.
          const ambiguous = !opening && !isSpace(next(i, p)) && isWordChar(previous(i, p)) && isWordChar(next(i, p));
          const opens: boolean = ambiguous ? openAt === null : opening;
          problems.push({ kind: 'straight', offset: i, replacement: opens ? '“' : '”' });
          openAt = opens ? i : null;
        } else if (c === '“') {
          // Closing a speech: after a word (`fine!“`), or floating with a space after it (`single, “ I said`).
          if (openAt !== null && (!opening || isSpace(next(i, p)))) {
            problems.push({ kind: 'wrong-way', offset: i, replacement: '”' });
            openAt = null;
          } else {
            if (openAt !== null) problems.push({ kind: 'unclosed', offset: openAt, replacement: null });
            openAt = i;
          }
        } else if (openAt === null) {
          if (opening && !isSpace(next(i, p))) {
            problems.push({ kind: 'wrong-way', offset: i, replacement: '“' });
            openAt = i;
          } else if (i > p[0] && isDigit(s[i - 1])) {
            // Closing nothing, right after a number: inches (`a 26” wheel`).
            problems.push({ kind: 'prime', offset: i, replacement: '″' });
          } else {
            problems.push({ kind: 'unopened', offset: i, replacement: null });
          }
        } else {
          openAt = null;
        }
      } else if (c === "'") {
        const before = previous(i, p), after = next(i, p);
        let mark = '’';
        if (isSpace(before) || QUOTE_OPENERS.has(before!)) {
          // At the start of a word: an elision ('70s, 'til) or an opening single quote.
          let word = '';
          let j = i + 1;
          while (j < p[1] && isAlphabetic(s[j])) word += s[j++];
          const closed = j < p[1] && (s[j] === "'" || s[j] === '’'); // 'E' is a quote, not 'e
          const elided = isDigit(after) || (ELISIONS.has(word.toLowerCase()) && !closed);
          mark = elided ? '’' : '‘';
          if (!elided) singleOpened = true;
        }
        problems.push({ kind: 'straight', offset: i, replacement: mark });
      }
    }
    // Left open: fine if the next paragraph carries the speech on.
    if (openAt !== null) {
      let following: string | undefined;
      for (const q of paragraphs.slice(n + 1)) {
        const m = firstMark(q);
        if (m !== undefined) { following = m; break; }
      }
      if (following !== '“' && following !== '"') problems.push({ kind: 'unclosed', offset: openAt, replacement: null });
    }
  });
  return problems.sort((a, b) => a.offset - b.offset);
}

/** `QuoteMarks`, named as the Swift names it. */
export const QuoteMarks = {
  check: checkQuotes,
  fixed: fixedQuotes,
  analyse: analyseQuotes,
  elisions: ELISIONS,
  markers: QUOTE_MARKERS,
  openers: QUOTE_OPENERS,
  message: quoteProblemMessage,
};
